import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  usePostSetlist,
  usePutSetlist,
} from "../../../../api/mutations";
import {
  useGetMyAccount,
  useGetMyProfile,
  useGetMyWorkAutoSuggest,
  useGetSetlist,
  useGetSetlists,
} from "../../../../api/queries";
import useSiteConfigAuth from "../../../../api/sanity/queries/getSiteConfigWithCode";
import DoubleMultipleInput from "../../../../components/DoubleMultipleInput";
import FormError from "../../../../components/FormError";
import H2 from "../../../../components/H2";
import H4 from "../../../../components/H4";
import Input from "../../../../components/Input";
import LoadingSmall from "../../../../components/LoadingSmall";
import MultipleSelect from "../../../../components/MultipleSelect";
import RebrandPrimaryButton from "../../../../components/RebrandPrimaryButton";
import Spacer from "../../../../components/Spacer";
import TextArea from "../../../../components/TextArea";
import { sendEvent, sendProperty } from "../../../../helpers/helpers";
import useAppStore from "../../../../store/appStateZustand";
// import AppState from "../../../../store/appstate";

function SetlistsCreateUpdate() {
  const { id } = useParams();
  const history = useHistory();
  const { data: siteConfig } = useSiteConfigAuth();
  if (siteConfig.disable_gigs) {
    history.push("/");
  }
  const profile = useGetMyProfile();
  const isUpdate = !!id;
  const [songList, setSongList] = useState([]);
  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [name, setName] = useState();
  const [notes, setNotes] = useState();
  const [otherSongs, setOtherSongs] = useState([]);

  const [tuneCode] = useState("");
  const [items, setItems] = useState(null);
  const [searchString, setSearchString] = useState("");
  const [options, setOptions] = useState([]);
  const [cachedSongs, setCachedSongs] = useState([]);
  const [noOptionsMessage, setNoOptionsMessage] = useState(
    "Start typing to search...",
  );
  const createMutation = usePostSetlist();
  const updateMutation = usePutSetlist();

  const fetchOptions = useCallback((inputValue) => {
    const debouncedSearch = debounce((value) => {
      if (value.length > 0) {
        setSearchString(value);
      }
    }, 500);
    debouncedSearch(inputValue);
  }, []);

  const handleInputChange = (inputValue) => {
    fetchOptions(inputValue);
  };
  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);
  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const setlists = useGetSetlists({ ac: selectedAccount });
  const setlist = useGetSetlist({ ac: selectedAccount, id });
  const { t } = useTranslation("gigs");
  const works = useGetMyWorkAutoSuggest({
    aac: selectedAccount,
    searchstring: searchString,
  });

  useEffect(() => {
    if (works && works.data && works.data.works && Array.isArray(works.data.works)) {
      const newSongsFromWorks = works.data.works;

      const newFetchedOptions = newSongsFromWorks.map(songData => ({
        label: `${songData.title} - (${songData.performingartists})`,
        value: songData.workcode,
      }));

      // Conditionally update options to prevent loops
      setOptions((currentOptions) => {
        // Create signatures for comparison. Sorting helps if order isn't guaranteed.
        const currentSig = JSON.stringify(currentOptions.map(o => ({ l: o.label, v: o.value })).sort((a, b) => String(a.v).localeCompare(String(b.v))));
        const newSig = JSON.stringify(newFetchedOptions.map(o => ({ l: o.label, v: o.value })).sort((a, b) => String(a.v).localeCompare(String(b.v))));

        if (currentSig !== newSig) {
          return newFetchedOptions; // Update if different
        }
        return currentOptions; // Return same reference if no actual change
      });

      // Conditionally update cachedSongs
      setCachedSongs((prevCachedSongs) => {
        const existingWorkcodes = new Set(prevCachedSongs.map(s => s.workcode));
        const songsToAdd = newSongsFromWorks.filter(
          newSong => newSong.workcode && !existingWorkcodes.has(newSong.workcode),
        );

        if (songsToAdd.length > 0) {
          // Ensure we don't add duplicates if newSongsFromWorks itself has them
          const uniqueSongsToAdd = songsToAdd.filter((song, index, self) =>
            index === self.findIndex(s => s.workcode === song.workcode),
          );
          if (uniqueSongsToAdd.length > 0) {
            return [...prevCachedSongs, ...uniqueSongsToAdd];
          }
        }
        return prevCachedSongs; // Return same reference if no actual change
      });

      setNoOptionsMessage(
        newFetchedOptions.length === 0
          ? "No options found"
          : "Start typing to search...",
      );
    }
    else if (works && (works.isLoading === false && works.isFetching === false) && (!works.data || !works.data.works)) {
      // Handle case where 'works' has loaded/fetched but contains no items or unexpected structure
      setOptions((currentOptions) => {
        if (currentOptions.length > 0)
          return []; // Clear options if they had content
        return currentOptions; // Otherwise, no change if already empty
      });
      setNoOptionsMessage("No options found");
      // Optionally, clear cachedSongs if appropriate for this case
      // setCachedSongs(prevCached => prevCached.length > 0 ? [] : prevCached);
    }
  }, [works]); // Depend on the 'works' object. The internal logic now prevents
  // setState calls from causing loops if 'works' reference changes
  // but the underlying data for options/cachedSongs is the same.

  useEffect(() => {
    if (selectedAccount) {
      setlist.refetch();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount]);

  useEffect(() => {
    if (isUpdate && setlist.isSuccess && setlist.data && id && !name) {
      setName(setlist.data[0].name);
      setNotes(setlist.data[0].notes || "");
      console.log("Raw setlist data:", setlist.data[0].songs);
      const songs = setlist.data[0].songs
        ? JSON.parse(setlist.data[0].songs).map((s) => {
            const cachedSong = cachedSongs.find(
              song => song.workcode === s.workcode,
            );
            return {
              workcode: s.workcode,
              title: cachedSong ? cachedSong.title : s.title,
              performingartists: cachedSong
                ? cachedSong.performingartists
                : s.performingartists,
              label: cachedSong
                ? `${cachedSong.title} - (${
                  cachedSong.performingartists || s.performingartists
                })`
                : s.title,
            };
          })
        : [];
      setSongList(songs);
      setOtherSongs(
        setlist.data[0].other_songs
          ? JSON.parse(setlist.data[0].other_songs)
          : [],
      );
    }
  }, [isUpdate, setlist.isSuccess, setlist.data, id, name, cachedSongs]);

  if (
    setlist.isSuccess
    && setlist.data.length === 0
    && id
    && setlist.data.songs.workcode === null
  ) {
    history.push("/gigs/setlists");
  }

  function checkInvalidCoverItem(item, index) {
    const artistLength = item?.artist?.length;
    const songLength = item?.song?.length;
    const tuneCodeLength = item?.tunecode?.length;
    const validTuneCode = item?.tunecode?.length === 8;

    const errorStart = `${t("regex:cover-song-error-start")} ${index}:`;

    if (artistLength && !songLength) {
      setValidationErrorMessage(
        `${errorStart} ${t("regex:cover-song-song-title-error")}`,
      );
      return true;
    }

    if (songLength && !artistLength) {
      setValidationErrorMessage(
        `${errorStart} ${t("regex:cover-song-artist-name-error")}`,
      );
      return true;
    }

    if (tuneCodeLength && !validTuneCode) {
      setValidationErrorMessage(
        `${errorStart} ${t("regex:cover-song-tunecode-character-error")}`,
      );
      return true;
    }

    if (tuneCodeLength && validTuneCode && (!artistLength || !songLength)) {
      setValidationErrorMessage(
        `${errorStart} ${t("regex:cover-song-tunecode-both-error")}`,
      );
      return true;
    }

    setValidationErrorMessage("");
    return false;
  }

  const validateTuneCodeAndMandatoryFields = () => {
    const foundedError = items.find((item, i) =>
      checkInvalidCoverItem(item, i + 1),
    );
    if (foundedError)
      return false;
    return true;
  };

  const checkErrors = () => {
    const errs = {};
    let valid = true;

    // checks the validation of tuneCode
    valid = validateTuneCodeAndMandatoryFields();

    // has at least one song
    if (!songList || songList.length < 1) {
      errs.songList = t("regex:at-least-one-song-error");
      valid = false;
    }

    if (songList) {
      songList.forEach((sl) => {
        if (!sl.workcode) {
          errs.songList = t("regex:selected-every-song-error");
          valid = false;
        }
      });
    }

    if (!name || name.trim().length === 0) {
      errs.name = t("regex:unnamed-setlist-error");
      valid = false;
    }

    setErrors(errs);
    return valid;
  };

  const addSetlist = () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    const valid = checkErrors();
    // we need this much conditional stuff when creating a setlist because we need to
    // account for the older setlists from before the refactor that have a different JSON structure
    if (valid) {
      const selectedWorkcodes = songList.map(s => s.workcode);
      const selectedSongs = selectedWorkcodes.map((workcode) => {
        const song = songList.find(s => s.workcode === workcode);
        const cachedSong = cachedSongs.find(
          option => option.workcode === workcode,
        );
        return {
          workcode,
          title: song.title || (cachedSong ? cachedSong.title : ""),
        };
      });
      createMutation.reset();
      const data = {
        account_code: selectedAccount,
        originator_code: myAccount.data?.originatorcode?.code,
        name,
        notes,
        tuneCode,
        songs: selectedSongs ? JSON.stringify(selectedSongs) : null,
        other_songs: otherSongs ? JSON.stringify(otherSongs) : null,
      };

      createMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.failedvalidation) {
            setValidationErrorMessage(response.data.messages);
            setPending(false);
          }
          else {
            toast.success(t("gigs:successfully-submitted-setlist-toast"), {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            history.push("/gigs/setlists");
            setlist.refetch();
            setlists.refetch();
            setPending(false);
            sendProperty({
              property: "last_setlist_added",
              value: "CURRENT_DATE",
              email: profile?.data?.email,
            });
            sendEvent({ property: "pe14489857_setlist_added" });
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    else {
      setPending(false);
    }
  };

  const updateSetlist = () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    const valid = checkErrors();

    if (valid) {
      const selectedWorkcodes = songList.map(s => s.workcode);
      const selectedSongs = selectedWorkcodes.map((workcode) => {
        const song = songList.find(s => s.workcode === workcode);
        const cachedSong = cachedSongs.find(
          option => option.workcode === workcode,
        );
        return {
          workcode,
          title: song.title || (cachedSong ? cachedSong.title : ""),
        };
      });
      songList.forEach((s) => {
        if (!selectedSongs.find(p => s.workcode === p.workcode)) {
          selectedSongs.push(s);
        }
      });

      updateMutation.reset();
      const data = {
        id,
        account_code: selectedAccount,
        originator_code: myAccount.data?.originatorcode?.code,
        name,
        notes,
        tuneCode,
        songs: selectedSongs ? JSON.stringify(selectedSongs) : null,
        other_songs: otherSongs ? JSON.stringify(otherSongs) : null,
      };

      updateMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.failedvalidation) {
            setValidationErrorMessage(response.data.messages);
            setPending(false);
          }
          else {
            toast.success(t("gigs:successfully-updated-setlist-toast"), {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            history.push("/gigs/setlists");
            setlist.refetch();
            setlists.refetch();
            setPending(false);
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    else {
      setPending(false);
    }
  };

  return (
    <>
      <div className="flex-1 order-last xl:w-full bg-sentric-rebrand-light-grey min-h-50 lg:min-h-screen-minus-header">
        {isUpdate ? (
          <H2>{t("gigs:update-setlist-header")}</H2>
        ) : (
          <H2>{t("gigs:create-setlist-header")}</H2>
        )}
        <Spacer />
        {id && setlist.isLoading && <LoadingSmall />}
        {(!id || setlist.isSuccess) && (
          <div className="w-full lg:w-2/3">
            <H4>{t("gigs:setlist-name")}</H4>
            <Spacer thin />

            <Input
              id="txtSetlistName"
              required
              type="text"
              error={errors.name}
              placeholder={t("gigs:setlist-name")}
              value={name}
              onBlur={() => {}}
              onChange={(e) => {
                setName(e.target.value);
              }}
            />
            <Spacer />

            <H4>{t("gigs:add-songs-to-setlist")}</H4>

            <Spacer thin />

            {(isUpdate && songList) || !isUpdate ? (
              <MultipleSelect
                customLabel={t("gigs:select-song")}
                name="SetlistAddSong"
                type="text"
                addAnotherText={t("gigs:add-another-song")}
                valueKey="workcode"
                value={songList}
                onChange={(arr) => {
                  setSongList(arr);
                }}
                error={errors.songList}
                options={options}
                onInputChange={handleInputChange}
                noOptionsMessage={noOptionsMessage}
              />
            ) : null}

            <Spacer />
            <H4>{t("gigs:do-you-cover-any-songs")}</H4>
            <Spacer thin />

            <DoubleMultipleInput
              area="Setlist"
              type="text"
              addAnotherText={t("gigs:add-another")}
              error={errors.otherSongs}
              placeholder1={t("gigs:artist-placeholder")}
              placeholder2={t("gigs:songs-placeholder")}
              placeholder3={t("gigs:tunecode-placeholder")}
              valueKey1="artist"
              valueKey2="song"
              valueKey3="tunecode"
              value={otherSongs}
              onBlur={() => {}}
              onChange={(arr) => {
                setOtherSongs(arr);
              }}
              items={items}
              setItems={setItems}
            />

            <Spacer />
            <H4>{t("gigs:notes-header")}</H4>
            <Spacer thin />
            <TextArea
              id="txtSetlistNotes"
              className="w-full"
              type="textarea"
              error={errors.notes}
              placeholder={t("gigs:notes-placeholder")}
              value={notes}
              onBlur={() => {}}
              onChange={(e) => {
                setNotes(e.target.value);
              }}
            />

            <Spacer />
            <FormError
              mutationError={mutationErrorMessage}
              validationError={validationErrorMessage}
            />
            <div className="flex justify-center sm:justify-end">
              <RebrandPrimaryButton
                id={isUpdate ? "btnSetlistUpdate" : "btnSetlistAdd"}
                onClick={isUpdate ? updateSetlist : addSetlist}
                pending={pending}
                arrow
              >
                {isUpdate
                  ? t("gigs:update-setlist-button")
                  : t("gigs:add-setlist-button")}
              </RebrandPrimaryButton>
            </div>
            <Spacer />
          </div>
        )}
      </div>
    </>
  );
}
export default SetlistsCreateUpdate;
